// Wall-clock "live broadcast" simulation for YouTube tapes.
//
// The CRT pretends every YouTube tape is a continuously broadcasting channel:
// the first time the user tunes to it we mark the broadcast's epoch
// (`startedAt`); every subsequent tune-in seeks to
//   (now - startedAt)  mod  duration
// so the show feels like it's been running the whole time you were away.
//
// Duration is learned via the YouTube IFrame Player API on the first play and
// cached, so the modulo math is exact from the second tune-in onward.

const STORAGE_KEY = "crt_yt_broadcast";
// Conservative cap when duration is unknown — keep the start< parameter inside
// a range YouTube will accept rather than rolling into infinity.
const UNKNOWN_DURATION_CAP_SECONDS = 2 * 60 * 60;

function readMap() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
}

function writeMap(map) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); }
  catch (e) { /* localStorage full / unavailable — silent */ }
}

/** Ensure a broadcast epoch exists for this YT tape. Returns the cached entry. */
export function ensureBroadcast(videoId) {
  const map = readMap();
  if (!map[videoId]) {
    map[videoId] = { startedAt: Date.now(), duration: null };
    writeMap(map);
  }
  return map[videoId];
}

/** Cache the discovered duration so future tune-ins compute modulo correctly. */
export function recordDuration(videoId, duration) {
  if (!videoId || !Number.isFinite(duration) || duration <= 0) return;
  const map = readMap();
  if (!map[videoId]) map[videoId] = { startedAt: Date.now(), duration };
  else map[videoId].duration = Math.floor(duration);
  writeMap(map);
}

/** Compute the seconds offset to seek to for this tape's "current" broadcast moment. */
export function getBroadcastPosition(videoId) {
  const map = readMap();
  const entry = map[videoId];
  if (!entry) return 0;
  const elapsed = Math.max(0, Math.floor((Date.now() - entry.startedAt) / 1000));
  if (entry.duration && entry.duration > 0) {
    return elapsed % entry.duration;
  }
  return Math.min(elapsed, UNKNOWN_DURATION_CAP_SECONDS);
}

/** Wipe a tape's broadcast schedule — used when the tape is deleted. */
export function forgetBroadcast(videoId) {
  if (!videoId) return;
  const map = readMap();
  if (videoId in map) {
    delete map[videoId];
    writeMap(map);
  }
}
